// ScheduleDetail.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const ScheduleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [schedule, setSchedule] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [schRes, shiftRes] = await Promise.all([
          axios.get(`/schedules/${id}`),
          axios.get(`/shifts?schedule_id=${id}`)
        ]);

        setSchedule(schRes.data);

        // ✅ Frontend safety filter: only keep shifts belonging to this schedule
        const allShifts = shiftRes.data || [];
        const filtered = allShifts.filter(
          (s) => String(s.schedule_id) === String(id)
        );
        setShifts(filtered);

      } catch {
        toast.error('Failed to load schedule');
        navigate('/schedules');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id]);

  const statusBadge = (status) => {
    const map = {
      active:    ['#fff3e0', '#f1870e'],
      completed: ['#e3f2fd', '#1565c0'],
      cancelled: ['#ffebee', '#c62828']
    };
    const [bg, color] = map[status] || ['#f5f5f5', '#666'];
    return (
      <span style={{
        backgroundColor: bg, color,
        padding: '4px 12px', borderRadius: '12px',
        fontSize: '13px', fontWeight: 600
      }}>
        {status}
      </span>
    );
  };

  const fmt = (t) => {
    if (!t) return '—';
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const canEdit = ['super_admin', 'system_admin'].includes(user?.role);

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center',
        alignItems: 'center', height: '60vh',
        color: '#666', fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  if (!schedule) return null;

  return (
    <div style={{ padding: '32px', backgroundColor: '#f5f6fa', minHeight: '100vh' }}>

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <button
          onClick={() => navigate('/schedules')}
          style={{
            background: 'none', border: '1px solid #ddd',
            padding: '6px 14px', borderRadius: '8px',
            cursor: 'pointer', fontSize: '14px', color: '#555'
          }}
        >
          ← Back
        </button>
        <h2 style={{ color: '#f1870e', margin: 0, fontSize: '22px', fontWeight: 700 }}>
          {schedule.exam_name}
        </h2>
        {statusBadge(schedule.status)}
      </div>

      {/* INFO CARD */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '24px', marginBottom: '24px'
      }}>
        <h3 style={{ color: '#f1870e', marginBottom: '16px', fontSize: '16px', fontWeight: 700 }}>
          Schedule Details
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))',
          gap: '20px'
        }}>
          {[
            ['Exam Type',   schedule.exam_type        || '—'],
            ['Start Date',  formatDate(schedule.start_date)],
            ['End Date',    formatDate(schedule.end_date)],
            ['Center',      schedule.center_name      || '—'],
            ['Created By',  schedule.created_by_name  || '—']
          ].map(([label, value]) => (
            <div key={label}>
              <div style={{
                fontSize: '12px', color: '#888', fontWeight: 600,
                textTransform: 'uppercase', marginBottom: '4px'
              }}>
                {label}
              </div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#222' }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SHIFTS */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '24px'
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '20px'
        }}>
          <h3 style={{ color: '#f1870e', margin: 0, fontSize: '16px', fontWeight: 700 }}>
            Shifts ({shifts.length})
          </h3>
          {canEdit && (
            <button
              onClick={() => navigate(`/shifts/add?schedule_id=${id}`)}
              style={{
                backgroundColor: '#f1870e', color: 'white',
                border: 'none', padding: '8px 16px',
                borderRadius: '8px', cursor: 'pointer',
                fontWeight: 600, fontSize: '13px'
              }}
            >
              + Add Shift
            </button>
          )}
        </div>

        {shifts.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>🕐</div>
            No shifts added yet for this schedule.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1870e' }}>
                  {['#', 'Date', 'Shift', 'Time', 'Students', 'Rate (₹)', 'Holiday', 'Actions'].map(h => (
                    <th key={h} style={{
                      padding: '11px 14px', textAlign: 'left',
                      fontSize: '12px', color: 'white',
                      fontWeight: 700, textTransform: 'uppercase'
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shifts.map((s, i) => (
                  <tr key={s.id} style={{
                    borderBottom: '1px solid #f0f0f0',
                    background: i % 2 === 0 ? '#fff' : '#fafafa'
                  }}>
                    <td style={{ padding: '12px 14px', color: '#888' }}>{i + 1}</td>

                    <td style={{ padding: '12px 14px' }}>{formatDate(s.shift_date)}</td>

                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        backgroundColor: '#fff3e0', color: '#f1870e',
                        padding: '3px 10px', borderRadius: '12px',
                        fontSize: '12px', fontWeight: 600
                      }}>
                        {s.shift_name || `Shift ${s.shift_number}`}
                      </span>
                    </td>

                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      {fmt(s.start_time)} – {fmt(s.end_time)}
                    </td>

                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      {s.student_count ?? 0}
                    </td>

                    <td style={{ padding: '12px 14px', color: '#2e7d32', fontWeight: 600 }}>
                      ₹{Number(s.remuneration_rate ?? 0).toFixed(2)}
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        backgroundColor: s.is_holiday ? '#ffebee' : '#e8f5e9',
                        color: s.is_holiday ? '#c62828' : '#2e7d32',
                        padding: '3px 10px', borderRadius: '12px',
                        fontSize: '12px', fontWeight: 600
                      }}>
                        {s.is_holiday ? 'Yes' : 'No'}
                      </span>
                    </td>

                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => navigate(`/shifts/${s.id}/mapping`)}
                          style={{
                            backgroundColor: '#fff3e0', color: '#f1870e',
                            border: 'none', padding: '5px 10px',
                            borderRadius: '6px', fontSize: '12px',
                            fontWeight: 600, cursor: 'pointer'
                          }}
                        >
                          Mapping
                        </button>
                        <button
                          onClick={() => navigate(`/shifts/${s.id}/attendance`)}
                          style={{
                            backgroundColor: '#e3f2fd', color: '#1565c0',
                            border: 'none', padding: '5px 10px',
                            borderRadius: '6px', fontSize: '12px',
                            fontWeight: 600, cursor: 'pointer'
                          }}
                        >
                          Attendance
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => navigate(`/shifts/${s.id}/edit`)}
                            style={{
                              backgroundColor: '#e8f5e9', color: '#2e7d32',
                              border: 'none', padding: '5px 10px',
                              borderRadius: '6px', fontSize: '12px',
                              fontWeight: 600, cursor: 'pointer'
                            }}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* REMUNERATION BUTTON */}
      {canEdit && (
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => navigate(`/remuneration/${id}`)}
            style={{
              backgroundColor: '#f1870e', color: 'white',
              border: 'none', padding: '12px 28px',
              borderRadius: '8px', cursor: 'pointer',
              fontWeight: 700, fontSize: '15px'
            }}
          >
            💰 Calculate Remuneration
          </button>
        </div>
      )}

    </div>
  );
};

export default ScheduleDetail;